import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "@store/store";
import { Place } from "@dto/Place";
import { Event } from "@dto/Event";
import { Goal } from "@dto/Goal";
import { Loader } from "@ui-kit/Loader/Loader";
import { Tour } from "@dto/Tour";
import { FeedPost } from "@dto/FeedPost";
import { MenuItem } from "@dto/MenuItem";
import {
  selectMenuItem,
  selectPageNumber,
  selectSelectedCategories,
  selectSelectedExclusiveCategories,
  setPageNumberAction,
} from "@store/menuItem/menuItemReducer";
import { useInView } from "@hooks/useInView/useInView";
import {
  selectDatePickerDateRange,
  setDatePickerDateRangeAction,
} from "@store/datePickerReducer";
import {
  setSelectedCategoryIdsAction,
  setSelectedExclusiveCategoryIdsAction,
} from "@store/menuItem/menuItemReducer";
import {
  selectFilteredAds,
  selectLastImpressionIndex,
} from "@store/ad/adReducer";
import { selectAdNthPosition, selectClient } from "@store/client/clientReducer";
import { EntityListItem } from "../EntityListItem/EntityListItem";
import { Ad } from "@dto/Ad";
import { selectSelectedEntity, selectViewport } from "@store/map/mapReducer";
import { listEvents } from "@store/services/event";
import { listPlaces } from "@store/services/place";
import { listTours } from "@store/services/tour";
import { listGoals } from "@store/services/goal";
import { listFeedPosts } from "@store/services/feedPost";
import { ListEntitiesRequest } from "@store/apiTypes";
import { QueryActionCreatorResult } from "@reduxjs/toolkit/query";
import { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { TabButton } from "@ui-kit/TabButton/TabButton";

import "./EntityList.css";

type EntityListItemType = Place | Event | Goal | Tour | FeedPost | Ad;

type ListFunction = {
  initiate: (
    request: ListEntitiesRequest,
  ) => ThunkAction<QueryActionCreatorResult<any>, any, any, UnknownAction>;
};

/**
 * Factory to return the proper list entity function for the MenuItem
 */
const getListFunction = (menuItem: MenuItem): ListFunction => {
  if (menuItem.entity === "Place") {
    return listPlaces;
  }
  if (menuItem.entity === "Event") {
    return listEvents;
  }
  if (menuItem.entity === "Goal") {
    return listGoals;
  }
  if (
    menuItem.entity === "Tour" ||
    menuItem.entity === "Challenge" ||
    menuItem.entity === "SelfGuided"
  ) {
    return listTours;
  }
  if (menuItem.entity === "FeedPost") {
    return listFeedPosts;
  }

  return {
    initiate: () =>
      ({
        type: "query/initiate",
        data: [],
      }) as unknown as ThunkAction<
        QueryActionCreatorResult<any>,
        any,
        any,
        UnknownAction
      >,
  };
};

const PAGE_SIZE = VisitWidget.settings.entityPageSize;

const EntityList: FunctionComponent = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const page = useAppSelector(selectPageNumber);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const menuItem = useAppSelector(selectMenuItem);
  const dispatch = useAppDispatch();
  const [entities, setEntities] = useState<Array<EntityListItemType> | null>(
    null,
  );
  const client = useAppSelector(selectClient);
  const categories = useAppSelector(selectSelectedCategories);
  const exclusiveCategories = useAppSelector(selectSelectedExclusiveCategories);
  const dates = useAppSelector(selectDatePickerDateRange);
  const adNthPosition = useAppSelector(selectAdNthPosition);
  const ads = useAppSelector(selectFilteredAds);
  const lastImpressionIndex = useAppSelector(selectLastImpressionIndex);
  const lastImpressionAdIndexRef = useRef(lastImpressionIndex);
  const viewport = useAppSelector(selectViewport);
  const selectedEntity = useAppSelector(selectSelectedEntity);

  useEffect(() => {
    lastImpressionAdIndexRef.current = lastImpressionIndex;
  }, [lastImpressionIndex]);

  useEffect(() => {
    if (client.geographicPaginationEnabled && viewport && !selectedEntity) {
      // Reset page to 1 if map viewport changes and geographic paging is enabled.
      dispatch(setPageNumberAction(1));
    }
    // do not change when `selectedEntity` changes, we only want to know if its set when other
    // inputs change. We don't want to reset when viewport moves because entity is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, client.geographicPaginationEnabled, dispatch]);

  const prevPageRef = useRef(page);
  const { ref, isInView } = useInView();

  const categoriesKey = useMemo(() => {
    const allCategoryIds = [...categories, ...exclusiveCategories].map(
      (x) => x.id,
    );
    return allCategoryIds.join(",");
  }, [categories, exclusiveCategories]);

  const entitiesWithAds = useMemo(() => {
    if (!ads.length || !entities) {
      return entities;
    }

    const result: Array<EntityListItemType> = [];
    let adIndex = (lastImpressionAdIndexRef.current + 1) % ads.length;
    let entityCount = 0;

    for (let i = 0; i < entities.length; i++) {
      result.push(entities[i]);
      entityCount++;

      if (entityCount % (adNthPosition - 1) === 0 && ads.length > 0) {
        const ad = ads[adIndex % ads.length];
        result.push(ad);
        adIndex++;
      }
    }

    return result;
  }, [entities, ads, adNthPosition]);

  useEffect(() => {
    const runAsync = async () => {
      if (!menuItem) {
        return;
      }

      if (prevPageRef.current === page) {
        // assumes all other parameters cause page to reset to 0, so only if another parameter
        // causes this useEffect to run do we reset page.
        dispatch(setPageNumberAction(1));
        prevPageRef.current = 1;
      } else {
        prevPageRef.current = page;
      }

      if (page === 1) {
        setEntities(null);
      }

      setIsLoading(true);
      const listFunction = getListFunction(menuItem);
      const request = {
        categoryIds: categories.map((x) => x.id),
        exclusiveCategoryIds: exclusiveCategories.map((x) => x.id),
        dates,
        page,
        type: menuItem.entity,
        bbox: viewport?.bbox,
        geographicPaginationEnabled: client.geographicPaginationEnabled,
        includeAllTourTypes: menuItem.includeAllTourTypesEnabled,
      } as ListEntitiesRequest;

      const entitiesNextPage = (await dispatch(listFunction.initiate(request)))
        .data as EntityListItemType[];

      if (entitiesNextPage.length < PAGE_SIZE) {
        setHasMore(false);
        if (page === 1) {
          setEntities(entitiesNextPage);
        }
      } else {
        if (!hasMore) {
          setHasMore(true);
        }
        setEntities((prev) => {
          const nextEntities =
            prev && page > 1
              ? [...prev, ...entitiesNextPage]
              : entitiesNextPage;

          return nextEntities;
        });
      }
      setIsLoading(false);
    };
    runAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItem.id, page, categoriesKey, dates.endDate, dates.startDate]);

  useEffect(() => {
    if (isInView && hasMore) {
      dispatch(setPageNumberAction(prevPageRef.current + 1));
    }
  }, [isInView, hasMore, dispatch]);

  const handleClearFilters = useCallback(() => {
    dispatch(setPageNumberAction(1));
    dispatch(setDatePickerDateRangeAction({ startDate: null, endDate: null }));
    dispatch(setSelectedCategoryIdsAction([]));
    dispatch(setSelectedExclusiveCategoryIdsAction([]));
    setEntities(null);
  }, [dispatch]);

  return (
    <div
      className="entity-list-component"
      ref={containerRef}
      style={{ height: "100%" }}>
      {entitiesWithAds &&
        entitiesWithAds.map((entity, index) => (
          <EntityListItem
            entity={entity}
            key={`${entity.id}-${entity.type}-${index}`}
            menuItem={menuItem!}
            ref={index === entitiesWithAds.length - 1 ? ref : undefined}
          />
        ))}
      {isLoading && (
        <div className="loader-container">
          <Loader size="small" />
        </div>
      )}
      {!isLoading && (entities?.length ?? 0) === 0 && (
        <div className="no-entities-container">
          <p>No items found</p>
          <TabButton
            variant="primary"
            onPress={handleClearFilters}
            label="Clear filters"
          />
        </div>
      )}
    </div>
  );
};

export { EntityList };
export type { EntityListItemType };
